import React, { useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect, forwardRef, useImperativeHandle } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Row,
  Col,
  Card,
  Breadcrumb,
  Badge,
  Empty,
} from "antd";
import {
  PlusOutlined,
  HomeOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
} from "@ant-design/icons";
import Create from "./Create";
import Edit from "./Edit";
import View from "./View";
import CompanyAPI from "../../../restAPI/CompanyAPI";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { Resizable } from 'react-resizable';

const ResizableTitle = (props) => {
  const { onResize, width, children, ...restProps } = props;
  if (!width) return <th {...restProps}>{children}</th>;

  return (
    <th {...restProps}>
      <Resizable
        width={width}
        height={0}
        onResize={onResize}
        draggableOpts={{ enableUserSelectHack: false }}
        handle={
          <span
            style={{
              position: "absolute",
              right: 0,
              bottom: 0,
              cursor: "col-resize",
              width: "2px",
              height: "100%",
              background: "#ffeb3b3b",
            }}
          />
        }
      >
        <div>{children}</div>
      </Resizable>
    </th>
  );
};

const CompanyTable = forwardRef(function CompanyTable({ onEditDrawerClose }, ref) {
  const tableContainerRef = useRef();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const editRef = useRef();
  const viewRef = useRef();
  const { token } = useSelector((state) => state.auth);
  const [allCompanyForDisplay, setAllCompanyForDisplay] = useState([]);
  const [allCompany, setAllCompany] = useState([]);
  const { groupId, companyId } = useOutletContext();

  const searchInput = useRef();
  const createRef = useRef();

  const showChildDrawer = () => {
    if (createRef.current) {
      createRef.current.openDrawer();
    }
  };

  useImperativeHandle(ref, () => ({
    openEditDrawer: (companyId, stepIndex = 0) => {
      if (editRef.current && companyId) {
        editRef.id = companyId;
        editRef.current.openEditDrawer(stepIndex);
      }
    },
  }), []);

  useEffect(() => {
    getAllCompany();
  }, [groupId, companyId]);

  const refreshTable = () => {
    getAllCompany();
  };

  const getAllCompany = async () => {
    await CompanyAPI.getCompaniesForLoggedUser(
      token,
      localStorage.getItem("groupId"),
      localStorage.getItem("companyId")
    )
      .then((res) => {
        if (res?.data) {
          const rows = [];
          res.data.map((rowValue) => {
            let statusValue = null;
            if (rowValue.status) {
              statusValue = "Active";
            } else {
              statusValue = "Inactive";
            }
            rows.push({
              _id: rowValue._id,
              groupName: rowValue.name,
              country: rowValue.country.countryName.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
              email: rowValue.email,
              isBranch: rowValue.isBranch || false,
              status: statusValue,
              tin: rowValue.tin
            });
          });
          setAllCompanyForDisplay(rows);
          setAllCompany(res.data);
        }
      })
      .catch((err) => {
        console.log("error getting company data :", err.message);
      });
  };

  const getColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${placeholder}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText('');
    setSearchedColumn('');
    confirm();
  };

  const onEditChange = (id) => {
    if (editRef.current) {
      editRef.id = id;
      editRef.current.openEditDrawer();
    }
  };

  const onViewChange = (id) => {
    if (viewRef.current) {
      viewRef.id = id;
      viewRef.current.openViewDrawer();
    }
  };

  const initialColumns = [
    {
      title: "Company Name",
      dataIndex: "groupName",
      key: "groupName",
      ...getColumnSearchProps("groupName", "Company Name"),
      percentageWidth: 0.30,
    },
    {
      title: "TIN",
      dataIndex: "tin",
      key: "tin",
      percentageWidth: 0.30,
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      sorter: (a, b) => a.country.localeCompare(b.country),
      percentageWidth: 0.20,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      percentageWidth: 0.30,
    },
    {
      title: "Branch",
      dataIndex: "isBranch",
      key: "isBranch",
      percentageWidth: 0.10,
      align: "left",
      sorter: (a, b) => (a.isBranch === b.isBranch ? 0 : a.isBranch ? 1 : -1),
      render: (isBranch) => (
        <Badge
          status={isBranch ? "processing" : "default"}
          text={isBranch ? "Yes" : "No"}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      percentageWidth: 0.10,
      key: "status",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status) => (
        <Badge
          status={status === "Active" ? "success" : "error"}
          text={status}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      percentageWidth: 0.10,
      align: "center",
      render: (_, record) => (
        <Space
          size="small"
          style={{ display: "flex", justifyContent: "space-around" }}
        >
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={(e) => onViewChange(record._id)}
          />
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={(e) => onEditChange(record._id)}
            disabled={localStorage.getItem('groupId') === ""}
          />
        </Space>
      ),
    },
  ];

  const initialContainerWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;

  const [columns, setColumns] = useState(
    initialColumns.map(col => ({
      ...col,
      width: Math.floor(initialContainerWidth * col.percentageWidth),
    }))
  );

  const updateColumnWidths = useCallback(() => {
    if (tableContainerRef.current) {
      const containerWidth = tableContainerRef.current.offsetWidth;
      setColumns(initialColumns.map(col => ({
        ...col,
        width: Math.floor(containerWidth * col.percentageWidth),
      })));
    }
  }, []);

  useLayoutEffect(() => {
    updateColumnWidths();
  }, [updateColumnWidths]);

  useEffect(() => {
    window.addEventListener('resize', updateColumnWidths);
    return () => window.removeEventListener('resize', updateColumnWidths);
  }, [updateColumnWidths]);

  const handleResize = useCallback(
    (index) => (e, { size }) => {
      setColumns((prevColumns) => {
        const newColumns = [...prevColumns];
        newColumns[index] = { ...newColumns[index], width: size.width };
        return newColumns;
      });
    },
    []
  );

  const mergedColumns = useMemo(
    () =>
      columns.map((col, index) => ({
        ...col,
        onHeaderCell: (column) => ({
          width: column.width,
          onResize: handleResize(index),
        }),
      })),
    [columns, handleResize]
  );

  return (
    <>
      <div className="border-container">
        <Button
          type="primary"
          disabled={localStorage.getItem("groupId") === null || localStorage.getItem('groupId') === ""}
          variant="dashed"
          className="card-button"
          size="large"
          icon={<PlusOutlined />}
          onClick={showChildDrawer}
          shape="circle"
        />
        <Card title="Companies" bordered>
          <div
            style={{
              maxWidth: '100%',
              overflowX: 'auto',
            }}
          >
            <Table
              size="middle"
              components={{
                header: {
                  cell: ResizableTitle,
                },
              }}
              columns={mergedColumns}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No records found!"
                  />
                ),
              }}
              dataSource={allCompanyForDisplay}
              rowKey="_id"
              rowClassName={(record) => {
                // Header dropdown stores company _id in localStorage (context companyId is the display name)
                const selectedId = localStorage.getItem("companyId");
                if (
                  allCompanyForDisplay.length > 1 &&
                  selectedId &&
                  selectedId !== "" &&
                  selectedId !== "Select Company" &&
                  String(record._id) === String(selectedId)
                ) {
                  return "company-table-row-selected";
                }
                return "";
              }}
            />
          </div>
        </Card>
      </div>
      <Create ref={createRef} onRefresh={refreshTable} />
      <Edit ref={editRef} onRefresh={refreshTable} onDrawerClose={onEditDrawerClose} />
      <View ref={viewRef} onRefresh={refreshTable} />
    </>
  );
});

export default CompanyTable;
